import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { 
  Plus, 
  Search, 
  Trash2, 
  ShoppingBag, 
  User, 
  Printer, 
  MessageCircle, 
  Check, 
  Receipt, 
  ArrowLeft, 
  RotateCcw, 
  Phone, 
  MapPin, 
  X, 
  Layers
} from 'lucide-react';

const POSScreen = () => {
  const { getConfig } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success / WhatsApp Modal State
  const [createdOrder, setCreatedOrder] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [recipientPhone, setRecipientPhone] = useState('');

  const STORE_WHATSAPP = '9655506070';

  // Manual Bill Form State
  const [billForm, setBillForm] = useState({
    customerName: 'Walk-in Customer',
    customerPhone: '',
    customerEmail: '',
    customerAddress: 'Store Counter / Walk-in',
    paymentMethod: 'Cash',
    isPaid: true,
    discountAmount: 0,
    taxAmount: 0,
    shippingAmount: 0,
    orderItems: []
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [prodsRes, catsRes] = await Promise.all([
        axios.get(`${API_URL}/products`),
        axios.get(`${API_URL}/categories?showAll=true`)
      ]);
      setProducts(prodsRes.data);
      setCategories(catsRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading POS data:', err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Product Selection Handlers
  const handleAddProduct = (product) => {
    const existingIndex = billForm.orderItems.findIndex(i => i.product === product._id);
    if (existingIndex > -1) {
      const updated = [...billForm.orderItems];
      updated[existingIndex].qty += 1;
      setBillForm({ ...billForm, orderItems: updated });
    } else {
      const newItem = {
        name: product.name,
        price: product.price,
        qty: 1,
        image: product.image || '',
        product: product._id
      };
      setBillForm({ ...billForm, orderItems: [...billForm.orderItems, newItem] });
    }
  };

  const handleAddCustomItem = () => {
    const customItem = {
      name: 'Custom Product / Service',
      price: 100,
      qty: 1,
      image: '',
      product: null
    };
    setBillForm({ ...billForm, orderItems: [...billForm.orderItems, customItem] });
  };

  const handleUpdateQty = (index, delta) => {
    const updated = [...billForm.orderItems];
    const newQty = updated[index].qty + delta;
    if (newQty <= 0) {
      updated.splice(index, 1);
    } else {
      updated[index].qty = newQty;
    }
    setBillForm({ ...billForm, orderItems: updated });
  };

  const handleUpdateField = (index, field, value) => {
    const updated = [...billForm.orderItems];
    updated[index][field] = field === 'price' || field === 'qty' ? Number(value) : value;
    setBillForm({ ...billForm, orderItems: updated });
  };

  const handleRemoveItem = (index) => {
    const updated = billForm.orderItems.filter((_, i) => i !== index);
    setBillForm({ ...billForm, orderItems: updated });
  };

  const handleResetBill = () => {
    if (billForm.orderItems.length > 0 && !window.confirm('Clear all items and start a new bill?')) {
      return;
    }
    setBillForm({
      customerName: 'Walk-in Customer',
      customerPhone: '',
      customerEmail: '',
      customerAddress: 'Store Counter / Walk-in',
      paymentMethod: 'Cash',
      isPaid: true,
      discountAmount: 0,
      taxAmount: 0,
      shippingAmount: 0,
      orderItems: []
    });
  };

  // Calculations
  const subtotal = billForm.orderItems.reduce((acc, item) => acc + (Number(item.price) * Number(item.qty)), 0);
  const grandTotal = Math.max(0, subtotal - Number(billForm.discountAmount) + Number(billForm.taxAmount) + Number(billForm.shippingAmount));

  // Form Submission
  const handleSaveBill = async (actionType = 'save') => {
    if (billForm.orderItems.length === 0) {
      alert('Please add at least one item to the bill before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        customerName: billForm.customerName.trim() || 'Walk-in Customer',
        customerPhone: billForm.customerPhone.trim(),
        customerEmail: billForm.customerEmail.trim(),
        customerAddress: billForm.customerAddress.trim() || 'Store Counter / Walk-in',
        orderItems: billForm.orderItems,
        paymentMethod: billForm.paymentMethod,
        itemsPrice: subtotal,
        discountPrice: Number(billForm.discountAmount) || 0,
        taxPrice: Number(billForm.taxAmount) || 0,
        shippingPrice: Number(billForm.shippingAmount) || 0,
        totalPrice: grandTotal,
        isPaid: billForm.isPaid,
      };

      const { data } = await axios.post(`${API_URL}/orders/manual`, payload, getConfig());
      setCreatedOrder(data);
      setRecipientPhone((data.customerPhone || billForm.customerPhone || '').replace(/\D/g, ''));

      if (actionType === 'print') {
        window.open(`/orders/${data._id}/invoice`, '_blank');
      } else if (actionType === 'whatsapp') {
        sendDirectWhatsApp(data);
      } else {
        setShowSuccessModal(true);
      }

      // Reset bill for next customer
      setBillForm({
        customerName: 'Walk-in Customer',
        customerPhone: '',
        customerEmail: '',
        customerAddress: 'Store Counter / Walk-in',
        paymentMethod: 'Cash',
        isPaid: true,
        discountAmount: 0,
        taxAmount: 0,
        shippingAmount: 0,
        orderItems: []
      });

    } catch (err) {
      console.error('Error creating POS bill:', err);
      alert(err.response?.data?.message || 'Failed to save bill');
    } finally {
      setIsSubmitting(false);
    }
  };

  // WhatsApp Message Generator
  const generateWhatsAppMessage = (order) => {
    if (!order) return '';
    const orderId = order._id ? order._id.substring(0, 10).toUpperCase() : '';
    const dateStr = new Date(order.createdAt || Date.now()).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const customerName = order.customerName || 'Customer';
    const address = order.shippingAddress?.address || 'Store Counter';

    const itemsText = (order.orderItems || [])
      .map(
        (item, idx) =>
          `${idx + 1}. *${item.name}*\n   Qty: ${item.qty} × ₹${Number(item.price).toFixed(2)} = *₹${(item.qty * item.price).toFixed(2)}*`
      )
      .join('\n\n');

    const discountText =
      order.discountPrice > 0
        ? `\n*Discount:* -₹${Number(order.discountPrice).toFixed(2)}`
        : '';

    const invoiceUrl = `${window.location.origin}/orders/${order._id}/invoice`;

    return `🌿 *ALAIAATHI - Homemade Soaps & Products*
📞 *Contact:* +91 ${STORE_WHATSAPP} | ✉️ support@alaiaathi.com
━━━━━━━━━━━━━━━━━━━━━━━━
🧾 *TAX INVOICE / BILL RECEIPT*
*Receipt No:* #INV-${orderId}
*Date:* ${dateStr}
*Customer:* ${customerName}
*Status:* ${order.isPaid ? 'PAID ✅' : 'PENDING ⏳'}
━━━━━━━━━━━━━━━━━━━━━━━━
${itemsText ? `🛍️ *ORDER ITEMS:*\n\n${itemsText}\n━━━━━━━━━━━━━━━━━━━━━━━━\n` : ''}*Subtotal:* ₹${(order.itemsPrice || 0).toFixed(2)}
*Tax:* ₹${(order.taxPrice || 0).toFixed(2)}${discountText}
*Shipping:* ₹${(order.shippingPrice || 0).toFixed(2)}
💰 *TOTAL AMOUNT:* *₹${(order.totalPrice || 0).toFixed(2)}*
━━━━━━━━━━━━━━━━━━━━━━━━
📍 *Location:* ${address}

✨ *Thank you for shopping with Alaiaathi!*
For inquiries, message us at +91 ${STORE_WHATSAPP}.

🔗 *Online Bill & Receipt:*
${invoiceUrl}`;
  };

  const sendDirectWhatsApp = (order) => {
    let phone = (order.customerPhone || recipientPhone || '').replace(/\D/g, '');
    if (!phone) {
      setShowSuccessModal(true);
      return;
    }
    if (phone.length === 10) phone = `91${phone}`;
    else if (phone.startsWith('0') && phone.length === 11) phone = `91${phone.substring(1)}`;

    const message = generateWhatsAppMessage(order);
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`, '_blank');
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = 
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) || 
      p.brand?.toLowerCase().includes(productSearch.toLowerCase());
    const matchesCat = 
      selectedCategory === 'ALL' || 
      p.category?._id === selectedCategory || 
      p.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto">
      {/* Top Header Bar */}
      <div className="flex flex-wrap gap-4 justify-between items-center bg-white p-5 rounded-3xl border border-secondary-100 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="bg-emerald-100 p-3 rounded-2xl text-emerald-700">
            <Receipt className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-black italic text-secondary-900 uppercase tracking-tight">
              POS Counter Billing
            </h1>
            <p className="text-secondary-400 text-xs font-medium">
              Create instant point-of-sale invoices, print on Champ RP thermal machine, and WhatsApp receipts.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleResetBill}
            className="flex items-center space-x-1.5 px-4 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-xl font-bold text-xs transition-all"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>New / Clear Bill</span>
          </button>

          <Link
            to="/admin/billing"
            className="flex items-center space-x-1.5 px-4 py-2.5 bg-secondary-50 hover:bg-primary-50 hover:text-primary-600 border border-secondary-200 text-secondary-600 rounded-xl font-bold text-xs transition-all"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Invoice History</span>
          </Link>
        </div>
      </div>

      {/* Main Grid: Left Catalog & Customer (5 cols) / Right Bill Details & Total (7 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* ========================================================= */}
        {/* LEFT COLUMN: Customer Info + Product Catalog (5 cols) */}
        {/* ========================================================= */}
        <div className="lg:col-span-5 space-y-5">
          
          {/* Customer Details Card */}
          <div className="bg-white p-5 rounded-3xl border border-secondary-100 shadow-sm space-y-3.5">
            <h2 className="text-xs font-black uppercase tracking-wider text-secondary-900 flex items-center space-x-2">
              <User className="h-4 w-4 text-emerald-600" />
              <span>Customer Information</span>
            </h2>

            <div className="space-y-2.5">
              <div>
                <label className="block text-[11px] font-bold text-secondary-500 mb-1">Customer Name</label>
                <input 
                  type="text"
                  value={billForm.customerName}
                  onChange={(e) => setBillForm({ ...billForm, customerName: e.target.value })}
                  placeholder="e.g. Walk-in Customer / Ramesh"
                  className="w-full bg-secondary-50 border border-secondary-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-secondary-900 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[11px] font-bold text-secondary-500 mb-1 flex items-center space-x-1">
                    <Phone className="h-3 w-3 text-emerald-600" />
                    <span>WhatsApp Number</span>
                  </label>
                  <input 
                    type="tel"
                    value={billForm.customerPhone}
                    onChange={(e) => setBillForm({ ...billForm, customerPhone: e.target.value })}
                    placeholder="e.g. 9876543210"
                    className="w-full bg-secondary-50 border border-secondary-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-secondary-900 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-secondary-500 mb-1 flex items-center space-x-1">
                    <MapPin className="h-3 w-3 text-emerald-600" />
                    <span>Counter / Location</span>
                  </label>
                  <input 
                    type="text"
                    value={billForm.customerAddress}
                    onChange={(e) => setBillForm({ ...billForm, customerAddress: e.target.value })}
                    placeholder="e.g. Store Counter"
                    className="w-full bg-secondary-50 border border-secondary-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-secondary-900 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Product Catalog Card */}
          <div className="bg-white p-5 rounded-3xl border border-secondary-100 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xs font-black uppercase tracking-wider text-secondary-900 flex items-center space-x-2">
                <ShoppingBag className="h-4 w-4 text-emerald-600" />
                <span>Product Catalog</span>
              </h2>
              <button
                type="button"
                onClick={handleAddCustomItem}
                className="text-[11px] font-bold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1 rounded-xl border border-primary-200 transition-all flex items-center space-x-1"
              >
                <Plus className="h-3 w-3" />
                <span>Custom Item</span>
              </button>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3.5 top-3 h-4 w-4 text-secondary-400" />
              <input 
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search products by name or brand..."
                className="w-full pl-10 pr-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-2xl text-xs font-medium text-secondary-900 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Category Filter Pills */}
            <div className="flex gap-1.5 overflow-x-auto pb-1 text-[11px] font-bold">
              <button
                type="button"
                onClick={() => setSelectedCategory('ALL')}
                className={`px-3 py-1.5 rounded-xl transition-all ${
                  selectedCategory === 'ALL' 
                    ? 'bg-secondary-900 text-white shadow-sm' 
                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat._id}
                  type="button"
                  onClick={() => setSelectedCategory(cat._id)}
                  className={`px-3 py-1.5 rounded-xl whitespace-nowrap transition-all ${
                    selectedCategory === cat._id 
                      ? 'bg-secondary-900 text-white shadow-sm' 
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Product Items List (Click to Add) */}
            <div className="max-h-[380px] overflow-y-auto space-y-2 pr-1">
              {loading ? (
                <p className="text-center py-8 text-xs text-secondary-400 font-bold">Loading inventory...</p>
              ) : filteredProducts.length === 0 ? (
                <p className="text-center py-8 text-xs text-secondary-400 font-bold">No products match your search.</p>
              ) : (
                filteredProducts.map(prod => (
                  <div 
                    key={prod._id}
                    onClick={() => handleAddProduct(prod)}
                    className="flex items-center justify-between p-3 bg-secondary-50/70 hover:bg-emerald-50 hover:border-emerald-200 border border-secondary-100 rounded-2xl cursor-pointer transition-all group"
                  >
                    <div className="flex items-center space-x-3">
                      {prod.image ? (
                        <img src={prod.image} alt={prod.name} className="w-10 h-10 rounded-xl object-cover shadow-sm bg-white" />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-secondary-200 flex items-center justify-center text-secondary-500">
                          <Layers className="h-5 w-5" />
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-bold text-secondary-900 group-hover:text-emerald-800 line-clamp-1">{prod.name}</p>
                        <div className="flex items-center space-x-2 text-[10px] text-secondary-400">
                          <span>Stock: {prod.countInStock}</span>
                          {prod.mrp > prod.price && (
                            <span className="line-through text-secondary-300">₹{prod.mrp}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-black text-secondary-900 group-hover:text-emerald-700">₹{prod.price}</span>
                      <span className="p-1.5 bg-white group-hover:bg-emerald-600 group-hover:text-white rounded-lg text-secondary-400 shadow-sm transition-colors">
                        <Plus className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>

        {/* ========================================================= */}
        {/* RIGHT COLUMN: Active Bill Cart & Totals (7 cols) */}
        {/* ========================================================= */}
        <div className="lg:col-span-7 space-y-5">
          <div className="bg-white p-6 rounded-3xl border border-secondary-100 shadow-sm space-y-5">
            
            {/* Bill Header */}
            <div className="flex justify-between items-center pb-3 border-b border-secondary-100">
              <div>
                <h2 className="text-sm font-black uppercase tracking-wider text-secondary-900">
                  Bill Line Items ({billForm.orderItems.length})
                </h2>
                <p className="text-[11px] text-secondary-400 font-medium">Customer: <strong className="text-secondary-700">{billForm.customerName || 'Walk-in'}</strong></p>
              </div>
              <button
                type="button"
                onClick={handleAddCustomItem}
                className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center space-x-1"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add Row</span>
              </button>
            </div>

            {/* Line Items Table */}
            {billForm.orderItems.length === 0 ? (
              <div className="py-16 text-center text-secondary-400 space-y-3">
                <ShoppingBag className="h-10 w-10 mx-auto opacity-30 text-secondary-400" />
                <p className="text-xs font-bold">Your bill is empty.</p>
                <p className="text-[11px] text-secondary-400">Click any product from the catalog on the left to add it.</p>
              </div>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
                {billForm.orderItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-secondary-50/80 rounded-2xl gap-2 text-xs border border-secondary-100">
                    <div className="flex-1 min-w-[140px]">
                      <input 
                        type="text"
                        value={item.name}
                        onChange={(e) => handleUpdateField(idx, 'name', e.target.value)}
                        placeholder="Item Name"
                        className="w-full bg-white border border-secondary-200 rounded-xl px-3 py-1.5 text-xs font-bold text-secondary-900 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-1 bg-white border border-secondary-200 rounded-xl px-1.5 py-0.5">
                      <button 
                        type="button"
                        onClick={() => handleUpdateQty(idx, -1)}
                        className="px-2 py-1 font-black text-secondary-600 hover:bg-secondary-100 rounded text-xs"
                      >-</button>
                      <span className="w-6 text-center font-black">{item.qty}</span>
                      <button 
                        type="button"
                        onClick={() => handleUpdateQty(idx, 1)}
                        className="px-2 py-1 font-black text-secondary-600 hover:bg-secondary-100 rounded text-xs"
                      >+</button>
                    </div>

                    {/* Unit Price input */}
                    <div className="w-24">
                      <div className="relative">
                        <span className="absolute left-2.5 top-1.5 text-[11px] font-bold text-secondary-400">₹</span>
                        <input 
                          type="number"
                          value={item.price}
                          onChange={(e) => handleUpdateField(idx, 'price', e.target.value)}
                          className="w-full bg-white border border-secondary-200 rounded-xl pl-6 pr-2 py-1.5 text-xs font-black text-secondary-900 outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>

                    {/* Item Total */}
                    <span className="w-20 text-right font-black text-secondary-900">
                      ₹{(Number(item.price) * Number(item.qty)).toFixed(2)}
                    </span>

                    {/* Remove */}
                    <button 
                      type="button"
                      onClick={() => handleRemoveItem(idx)}
                      className="p-2 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Calculations & Payment Options */}
            <div className="pt-4 border-t border-secondary-100 space-y-3 text-xs font-bold text-secondary-600">
              <div className="flex justify-between items-center text-sm">
                <span>Items Subtotal:</span>
                <span className="font-black text-secondary-900">₹{subtotal.toFixed(2)}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-[10px] uppercase font-black text-secondary-500 mb-1">Discount (₹)</label>
                  <input 
                    type="number"
                    value={billForm.discountAmount}
                    onChange={(e) => setBillForm({ ...billForm, discountAmount: e.target.value })}
                    placeholder="0"
                    className="w-full bg-secondary-50 border border-secondary-200 rounded-xl px-3 py-2 text-xs font-bold text-secondary-900"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-black text-secondary-500 mb-1">Tax / GST (₹)</label>
                  <input 
                    type="number"
                    value={billForm.taxAmount}
                    onChange={(e) => setBillForm({ ...billForm, taxAmount: e.target.value })}
                    placeholder="0"
                    className="w-full bg-secondary-50 border border-secondary-200 rounded-xl px-3 py-2 text-xs font-bold text-secondary-900"
                  />
                </div>
              </div>

              {/* Payment Method & Status */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-[10px] uppercase font-black text-secondary-500 mb-1">Payment Method</label>
                  <select
                    value={billForm.paymentMethod}
                    onChange={(e) => setBillForm({ ...billForm, paymentMethod: e.target.value })}
                    className="w-full bg-secondary-50 border border-secondary-200 rounded-xl px-3 py-2.5 text-xs font-bold text-secondary-900"
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI / GPay / PhonePe">UPI / GPay / PhonePe</option>
                    <option value="Card">Credit / Debit Card</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="COD">Cash on Delivery</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] uppercase font-black text-secondary-500 mb-1">Payment Status</label>
                  <select
                    value={billForm.isPaid ? 'true' : 'false'}
                    onChange={(e) => setBillForm({ ...billForm, isPaid: e.target.value === 'true' })}
                    className="w-full bg-secondary-50 border border-secondary-200 rounded-xl px-3 py-2.5 text-xs font-bold text-secondary-900"
                  >
                    <option value="true">PAID (Completed)</option>
                    <option value="false">PENDING (Unpaid)</option>
                  </select>
                </div>
              </div>

              {/* Grand Total Box */}
              <div className="flex justify-between items-center p-4 bg-emerald-50 rounded-2xl border border-emerald-100 text-secondary-900 mt-2">
                <span className="font-black text-sm uppercase">Grand Total:</span>
                <span className="text-3xl font-black text-emerald-600">₹{grandTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <button
                type="button"
                disabled={isSubmitting || billForm.orderItems.length === 0}
                onClick={() => handleSaveBill('print')}
                className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white py-3.5 px-3 rounded-2xl font-black text-xs uppercase flex items-center justify-center space-x-1.5 shadow-lg shadow-primary-200 transition-all cursor-pointer"
              >
                <Printer className="h-4 w-4" />
                <span>Save & Print</span>
              </button>

              <button
                type="button"
                disabled={isSubmitting || billForm.orderItems.length === 0}
                onClick={() => handleSaveBill('whatsapp')}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-3.5 px-3 rounded-2xl font-black text-xs uppercase flex items-center justify-center space-x-1.5 shadow-lg shadow-emerald-200 transition-all cursor-pointer"
              >
                <MessageCircle className="h-4 w-4" />
                <span>Save & WhatsApp</span>
              </button>

              <button
                type="button"
                disabled={isSubmitting || billForm.orderItems.length === 0}
                onClick={() => handleSaveBill('save')}
                className="bg-secondary-900 hover:bg-secondary-800 disabled:opacity-50 text-white py-3.5 px-3 rounded-2xl font-black text-xs uppercase flex items-center justify-center space-x-1.5 shadow-lg transition-all cursor-pointer"
              >
                <Check className="h-4 w-4" />
                <span>{isSubmitting ? 'Saving...' : 'Save Bill Only'}</span>
              </button>
            </div>

          </div>
        </div>

      </div>

      {/* ========================================================= */}
      {/* Bill Created Success Modal */}
      {/* ========================================================= */}
      {showSuccessModal && createdOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full text-secondary-900 shadow-2xl relative animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-5 right-5 text-secondary-400 hover:text-secondary-700 transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-emerald-100 p-3 rounded-2xl text-emerald-600">
                <Check className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight">Bill Generated Successfully!</h3>
                <p className="text-xs text-secondary-500 font-sans">
                  Invoice ID: <strong className="text-secondary-900">INV-{createdOrder._id.substring(0, 8).toUpperCase()}</strong>
                </p>
              </div>
            </div>

            <div className="space-y-4 font-sans text-xs">
              <div className="p-3.5 bg-secondary-50 rounded-2xl border border-secondary-100 space-y-1">
                <div className="flex justify-between">
                  <span className="text-secondary-500 font-bold">Customer:</span>
                  <span className="font-bold text-secondary-900">{createdOrder.customerName || 'Walk-in Customer'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-500 font-bold">Total Amount:</span>
                  <span className="font-black text-emerald-600 text-sm">₹{createdOrder.totalPrice}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-500 font-bold">Status:</span>
                  <span className="font-bold text-emerald-600">{createdOrder.isPaid ? 'PAID' : 'PENDING'}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-secondary-600 mb-1.5">
                  Send WhatsApp to Customer
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-secondary-400">
                    <Phone className="h-4 w-4" />
                  </div>
                  <input
                    type="tel"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="Enter customer WhatsApp number..."
                    className="w-full pl-10 pr-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-2xl text-xs font-bold text-secondary-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <a
                  href={`/orders/${createdOrder._id}/invoice`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-primary-600 hover:bg-primary-500 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs transition-colors shadow-lg shadow-primary-200"
                >
                  <Printer className="h-4 w-4" />
                  <span>Print Receipt</span>
                </a>

                <button
                  type="button"
                  onClick={() => sendDirectWhatsApp({ ...createdOrder, customerPhone: recipientPhone })}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs transition-colors shadow-lg shadow-emerald-200"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSScreen;
